/**
 * The Solar Design copy deck (SD-2.2, EXPERIENCE.md §D).
 *
 * These strings are SPECIFIED, not written at the call site. FR-12 and FR-17 make the attribution
 * and disclaimer legal surface rather than UI polish, and the empty/error strings set an accuracy
 * expectation that the whole tier is positioned on. A string retyped slightly differently in a
 * second component is a different promise to a customer.
 *
 * `solarDesignCopy.test.ts` pins every value against EXPERIENCE.md §D character for character. If
 * the spec changes, change it here and the test tells you; if someone edits a string in passing,
 * the test tells them.
 *
 * Strings carrying a placeholder are exposed as FUNCTIONS rather than templates with a
 * find-and-replace at the call site, so a caller cannot forget to substitute and ship `{n}` to a
 * customer.
 */

/** §D.1 Non-removable. Present on every surface that shows design numbers (FR-12, FR-17). */
export const NON_REMOVABLE = Object.freeze({
  /**
   * CORRECTED 2026-08-05 (SD-7.5): the `Source:` prefix is required and was missing.
   *
   * FR-12 said the wording is "confirmed against Google's current attribution guidelines at
   * implementation", and when SD-7.5 finally did that check, Google's Solar API policy required
   * `Source: Includes solar data from Google`. Approved by Allan the same day.
   *
   * Every surface renders this same value — canvas footer, thumbnail, proposal footer, and the
   * corner baked into new snapshots — because they are one legal claim about one design. Snapshot
   * images stored before the change keep the old string: it is rasterized into the PNG and no code
   * change reaches it.
   */
  attribution: 'Source: Includes solar data from Google',
  /**
   * SD-7.5. NEW STRING — ⚠️ NOT YET REVIEWED BY ALLAN, like `provider_rate_limited` (SD-2.9) and
   * `layout_version_finalized` (SD-4.1).
   *
   * DESIGN.md §150 already required this and the deck never had it: the manual-trace path draws
   * over Mapbox Satellite, "and imagery attribution changes, since Mapbox requires its own
   * attribution rather than Google's. The FR-12 attribution region therefore carries a
   * per-provider string, not a constant." A traced design crediting Google is a false statement on
   * a legal surface.
   *
   * Content is Mapbox's requirement, not this deck's register: both Mapbox and OpenStreetMap must
   * be credited, `© Mapbox, © OpenStreetMap` is their documented form for print without clickable
   * links, and © Maxar is additionally required for any Satellite style. SD-5.1 renders the tiles
   * and should confirm against the style it picks.
   */
  attribution_trace: '© Mapbox, © OpenStreetMap, © Maxar',
  estimate_chip: 'Estimate',
  disclaimer_short:
    'Pre-qualification estimate from remote imagery. Not a site survey or engineering document.',
  disclaimer_full:
    'Pre-qualification estimate modeled from remote imagery. Not a site survey and not an engineering document. Final system size, production, and savings require a site visit.',
} as const);

/** The two providers a design's `provenance.provider` can name (roof_model_v1, AD-1). */
export type DesignProvider = 'google_solar' | 'manual_trace';

/**
 * The attribution lines for a design's provider (FR-12, AD-19, UX §D.1).
 *
 * An ARRAY because AD-19 types it `attribution: string[]` — a provider may need more than one
 * credit line, and Mapbox nearly does. Returning an array from one place is also what stops a
 * caller reaching for `NON_REMOVABLE.attribution` directly and hard-coding Google onto a traced
 * design, which is the failure DESIGN.md §150 names.
 *
 * An UNKNOWN provider returns an empty array rather than falling back to Google. A wrong credit is
 * worse than a missing one: crediting Google for imagery Google did not supply is a false claim,
 * where an empty region is merely incomplete and visibly so.
 */
export function attributionFor(provider: string | null | undefined): readonly string[] {
  if (provider === 'google_solar') return [NON_REMOVABLE.attribution];
  if (provider === 'manual_trace') return [NON_REMOVABLE.attribution_trace];
  return [];
}

/**
 * `Imagery ©{year} Google · Captured {Mon YYYY}`.
 *
 * Takes the already-formatted capture label rather than a Date, because the month format is part
 * of the specified string and deriving it here would put a locale between the spec and the screen.
 */
export function imageryDateLine(year: number | string, capturedMonYYYY: string): string {
  return `Imagery ©${year} Google · Captured ${capturedMonYYYY}`;
}

/** §D.2 Empty and error. */
export const EMPTY_AND_ERROR = Object.freeze({
  empty_title: 'No designs yet',
  empty_body:
    'Joules reads this address from above and places panels on the roof faces that get the most sun. It takes about twenty seconds.',
  empty_primary: 'Create a design',
  empty_secondary: 'Trace the roof myself',
  no_building:
    'We could not find a building at this address. Drag the pin to the roof, or trace it yourself.',
  no_address:
    'This project has no address we can locate, so automatic roof data is not available. You can still trace the roof yourself.',
  low_quality:
    'The imagery for this address is low resolution. Treat this design as a rough estimate and confirm on site.',
  no_usable_faces:
    'No roof face here gets enough sun for a worthwhile system. You can still place panels if you know something the imagery does not.',
  no_flux:
    'Sun data is unavailable for this address. Production is estimated from pitch and direction only.',
  provider_error: 'We could not reach the roof data service. Your layout is saved.',
  /**
   * SD-2.9. NEW STRING — ⚠️ NOT YET REVIEWED BY ALLAN, unlike the rest of this deck.
   *
   * AD-16 makes `provider_rate_limited` a code distinct from `timeout` and `provider_error`
   * because the client behaviour differs: a rate limit clears on its own and is worth retrying on
   * a schedule, where a provider error is not worth retrying without a change. Until SD-2.9 the
   * client collapsed all three into `provider_error` above, which told a rep "we could not reach
   * the service" when the truthful statement is "we are asking too fast, wait a moment" — and
   * discarded the `retry_after_s` the server sends precisely so the wait can be stated.
   *
   * Written in the deck's register: what happened, and what still works.
   */
  provider_rate_limited:
    'The roof data service is busy right now. Your layout is saved — try again in a moment.',
  offset_unknown: 'Add their annual usage to see bill offset',
  /**
   * SD-4.1. NEW STRING — ⚠️ NOT YET REVIEWED BY ALLAN, like `provider_rate_limited` above.
   *
   * AD-4 freezes a version once `finalized_at` is set, and `trg_solar_design_versions_immutable`
   * raises on any UPDATE to it. So a rep can click a panel on a finalized design, see it toggle,
   * and have the write refused. The string has to say WHICH of the two happened: "we could not
   * save" would send someone looking for an outage that is not there, which is exactly the mistake
   * SD-2.9 found in the rate-limit path.
   *
   * Written in the deck's register — what happened, and what to do next, with no apology.
   */
  layout_version_finalized:
    'This design is final, so layout changes are not being saved. Duplicate it to keep editing.',
  save_no_permission:
    'You can see these but not change them. Changing design defaults needs workspace admin.',
  gate_superadmin_only: 'Only a Joules superadmin can change who uses designs.',
  save_uninitialised:
    'This workspace has no settings record yet, so design defaults cannot be saved.',
  /**
   * SD-5.1. NEW STRINGS — ⚠️ NOT YET REVIEWED BY ALLAN, like `provider_rate_limited` (SD-2.9),
   * `layout_version_finalized` (SD-4.1) and `attribution_trace` (SD-7.5).
   *
   * The trace surface has two ways to open without tiles, and they are not the same kind of
   * problem, so one sentence covering both would tell a rep the wrong thing half the time.
   *
   * `trace_no_center` is a DATA state, and on today's data it is the common case rather than an
   * edge: `projects.lat`/`lng` are populated on 19 of 4,925 rows, so 4,893 projects have a street
   * and no coordinates. It deliberately does not say what to do next — a departure from §D.3's
   * rule, because SD-5.1 has no way to supply a coordinate and SD-5.4 owns the entry points that
   * will. SD-5.4 should revise it rather than leave a dead end standing.
   *
   * `trace_no_basemap` is a DEPLOY state and is the failure this story is most likely to ship:
   * `frontend/.env` is gitignored and `netlify.toml` never mentions Mapbox, so a missing Netlify
   * variable builds clean and renders a blank map. It says "setup" so a rep stops looking for a
   * problem with their project, which is the same distinction SD-2.9 drew for the rate limit.
   */
  trace_no_center:
    'We do not have coordinates for this address, so there is no imagery to trace over yet.',
  trace_no_basemap:
    'Satellite imagery is not configured, so there is nothing to trace over. This is a setup issue rather than a problem with this project.',
} as const);

/** `This workspace has reached its daily limit of {n} new designs. Tracing a roof yourself still works.` */
export function quotaMessage(n: number): string {
  return `This workspace has reached its daily limit of ${n} new designs. Tracing a roof yourself still works.`;
}

/**
 * The per-DESIGN re-resolve cap, which is a different limit from the workspace one.
 *
 * SD-2.9. ⚠️ NOT YET REVIEWED BY ALLAN. `quotaMessage` above names WORKSPACE designs and is
 * simply the wrong sentence for a design that has been re-resolved five times today — it would
 * tell a rep their workspace is out of designs when it is not, and send them to trace a roof by
 * hand when opening a different design would have worked. The backend distinguishes the two with
 * `scope`; this is the second sentence that distinction exists to make possible.
 */
export function designQuotaMessage(n: number): string {
  return `This design has been re-run ${n} times today, which is the daily limit for one design. Other designs still work, and this one resets after midnight UTC.`;
}

/** `Saved. New designs will use a {n} in ridge setback. Designs that already exist keep the values they were drawn with.` */
export function savedSetbackMessage(n: number): string {
  return `Saved. New designs will use a ${n} in ridge setback. Designs that already exist keep the values they were drawn with.`;
}

/**
 * §D.3 Units. System size is DC, production and savings are AC, and the surface SAYS so rather
 * than leaving it implied. Savings derived from DC would overstate by the measured 4.5% on every
 * design, and a systematic overstatement in the customer's favour is the one error a
 * pre-qualification tier cannot make.
 */
export const UNITS = Object.freeze({
  system_size: 'kW DC',
  production: 'kWh AC',
} as const);
